/* eslint-disable @typescript-eslint/no-explicit-any */
"use client";

import { AnimatePresence, type MotionProps, type AnimatePresenceProps } from "motion/react";
import { useEffect, useId, useRef, useCallback } from "react";
import { createStore, type StoreApi, useStore } from "zustand";
import { useShallow } from "zustand/react/shallow";
import * as m from "motion/react-m";
import { subscribeWithSelector } from "zustand/middleware";

export type StateProp<T, State extends string> = T | Partial<{ [Name in State]: T }>;

export type StateProps<Props, State extends string> = {
  [Key in keyof Props]: Key extends "key" ? Props[Key] : StateProp<Props[Key], State>;
};

type StoreWithSubscribeSelector<T> = StoreApi<T> & {
  subscribe: {
    (listener: (state: T, prevState: T) => void): () => void;
    <U>(
      selector: (state: T) => U,
      listener: (selected: U, prevSelected: U) => void,
      options?: {
        equalityFn?: (a: U, b: U) => boolean;
        fireImmediately?: boolean;
      },
    ): () => void;
  };
};

export function Motion<
  State extends string,
  Element extends keyof typeof m,
  Props = React.ComponentPropsWithRef<(typeof m)[Element]>,
>({
  store,
  update = [],
  render,
  wait = true,
  animatePresence = {},
  as = "div" as Element,
  ...props
}: StateProps<Props, State> & {
  store: StoreApi<Store<State>>;
  update?: Array<State>;
  render?: Array<State | `-${State}`>;
  wait?: boolean;
  as?: Element;
  animatePresence?: AnimatePresenceProps;
}) {
  const id = useId();
  const newProps: any = {};

  const isRendered = useStore(store, state => {
    if (!render) return true;
    const keys = state.states;
    let current = false;
    const res = [];
    for (const key of keys) {
      if (render.includes(key)) {
        current = true;
      } else if (render.includes(`-${key}`)) {
        current = false;
      }
      res.push(current);
    }
    return res[state.state] ?? false;
  });

  // re-render when we hit a state in the `update` array
  useStore(store, state => {
    let res;
    const keys = state.states;
    const usedKeys = update.sort((a, b) => keys.indexOf(a) - keys.indexOf(b));
    for (const key of usedKeys) {
      const index = keys.indexOf(key);
      if (index <= state.state) {
        res = key;
      } else {
        break;
      }
    }
    return res;
  });

  useEffect(() => {
    return () => {
      store.getState().reset(id);
    };
  }, [id, store]);

  const propKeys = useStore(
    store,
    useShallow(state => {
      const res: Partial<Record<keyof Props, State>> = {};
      for (const prop in props) {
        const value = props[prop as keyof typeof props];
        if (!value) continue;
        if (prop === "children") continue;
        if (
          typeof value === "string" ||
          typeof value === "boolean" ||
          typeof value === "number" ||
          typeof value === "function"
        ) {
          continue;
        }

        const propKeys = Object.keys(value) as Array<State>;
        if (propKeys[0] && propKeys[0].toUpperCase() !== propKeys[0]) {
          continue;
        }
        res[prop as keyof typeof props] = undefined;
        const keys = state.states;
        const usedKeys = propKeys.sort((a, b) => keys.indexOf(a) - keys.indexOf(b));
        for (const key of usedKeys) {
          const index = keys.indexOf(key);
          if (index <= state.state) {
            // By default we pick the latest value that is before or at the current state.
            res[prop as keyof typeof props] = key;
          } else {
            break;
          }
        }
      }
      return res;
    }),
  );
  const start = useStore(store, ({ start }) => start);
  const complete = useStore(store, ({ complete }) => complete);

  for (const prop in propKeys) {
    newProps[prop as any] = (props as any)[prop][(propKeys as any)[prop]];
  }

  const isInitialState = useStore(store, ({ state }) => state === 0);

  const eventHandlers: Partial<MotionProps> =
    wait && !isInitialState
      ? {
          onAnimationStart: (...args) => {
            if (typeof newProps.onAnimationStart === "function") {
              newProps.onAnimationStart(...args);
            } else if (typeof (props as any).onAnimationStart === "function") {
              (props as any).onAnimationStart(...args);
            }
            start(id);
          },
          onAnimationComplete: (...args) => {
            if (typeof newProps.onAnimationComplete === "function") {
              newProps.onAnimationComplete(...args);
            } else if (typeof (props as any).onAnimationComplete === "function") {
              (props as any).onAnimationComplete(...args);
            }
            complete(id);
          },
          onLayoutAnimationStart: (...args) => {
            if (typeof newProps.onLayoutAnimationStart === "function") {
              newProps.onLayoutAnimationStart(...args);
            } else if (typeof (props as any).onLayoutAnimationStart === "function") {
              (props as any).onLayoutAnimationStart(...args);
            }
            start(id + "-layout");
          },
          onLayoutAnimationComplete: (...args) => {
            if (typeof newProps.onLayoutAnimationComplete === "function") {
              newProps.onLayoutAnimationComplete(...args);
            } else if (typeof (props as any).onLayoutAnimationComplete === "function") {
              (props as any).onLayoutAnimationComplete(...args);
            }
            complete(id + "-layout");
          },
        }
      : {};

  const key = useStore(store, ({ key }) => key);
  const MotionComp = m[as] as any;
  const comp = <MotionComp key={key} {...props} {...newProps} {...eventHandlers} />;

  if (render) {
    return (
      <AnimatePresence key={key} {...animatePresence}>
        {isRendered ? comp : null}
      </AnimatePresence>
    );
  }

  return comp;
}

interface Store<State> {
  key: number;
  states: readonly State[];
  state: number;
  status: "idle" | "running" | "complete";
  inflight: Array<string>;
  start: (id: string) => void;
  complete: (id: string) => void;
  reset: (id: string) => void;
}

export function useAnimationSequence<State extends string>(states: readonly State[]) {
  const storeRef = useRef<StoreWithSubscribeSelector<Store<State>>>(null);

  if (!storeRef.current) {
    storeRef.current = createStore<Store<State>>()(
      subscribeWithSelector<Store<State>>(set => ({
        states,
        state: 0,
        key: 0,
        inflight: [],
        status: "idle" as const,
        start: id =>
          set(state => ({
            inflight: [...state.inflight, id].filter((i, index, a) => a.indexOf(i) === index),
          })),
        complete: id =>
          set(state => {
            const nextInflight = state.inflight.filter(i => i !== id);
            let nextState = state.state;
            let nextStatus: "running" | "complete" | "idle" = "running";
            if (nextInflight.length === 0) {
              nextState = nextState + 1;
              if (nextState >= states.length) {
                nextState = states.length - 1;
                nextStatus = "complete";
              }
            }
            return { inflight: nextInflight, state: nextState, status: nextStatus };
          }),
        reset: id =>
          set(state => ({
            inflight: state.inflight.filter(i => i !== id && i !== id + "-layout"),
          })),
      })),
    );
  }

  const motionRef = useRef<
    | {
        [Element in keyof typeof m]: (
          props: Omit<React.ComponentPropsWithRef<typeof Motion<State, Element>>, "as" | "store">,
        ) => React.JSX.Element;
      }
    | null
  >(null);

  if (!motionRef.current) {
    motionRef.current = {} as {
      [Element in keyof typeof m]: (
        props: Omit<React.ComponentPropsWithRef<typeof Motion<State, Element>>, "as" | "store">,
      ) => React.JSX.Element;
    };
    for (const el in m) {
      motionRef.current[el as keyof typeof m] = props => {
        return <Motion {...props} as={el as keyof typeof m} store={storeRef.current!} />;
      };
    }
  }

  const reset = useCallback(() => {
    storeRef.current!.setState({ key: Math.random(), state: 0, status: "idle" });
  }, []);

  const start = useCallback(() => {
    storeRef.current!.setState({ state: 1, status: "running" });
  }, []);

  return {
    motion: motionRef.current!,
    store: storeRef.current!,
    reset,
    start,
  };
}
